import { useState } from "react";
import {
  CheckCircle,
  Layers,
  ChevronRight,
  Zap,
} from "lucide-react";

const plans = [
  {
    id: "essential",
    name: "Essential",
    subtitle: "AI Smile Simulation Starter",
    desc: "For solo dental practices getting started with AI-powered smile simulation to boost case acceptance.",
    monthlyPrice: 99,
    annualPrice: 990,
    monthlyCredits: 40,
    annualCredits: 600,
    icon: Layers,
    cta: "Start Free Trial",
    ctaStyle: "primary",
    featured: false,
    mostPopular: false,
    features: [
      "40 credits / month — use for images (1 cr) or videos (3 cr), your choice",
      "Photorealistic smile previews based on patient's actual face",
      "Mobile-friendly — patients view, share & revisit from any device",
      "Email support with 24-hour response time",
      "Top up credits anytime — purchase extra credits as needed",
    ],
  },
  {
    id: "growth",
    name: "Growth",
    subtitle: "Most Popular for Implant Practices",
    desc: "For growing dental practices that need unlimited AI simulations, website lead capture, and case acceptance analytics.",
    monthlyPrice: 299,
    annualPrice: 2850,
    monthlyCredits: 400,
    annualCredits: 5000,
    icon: "/icons/growth.png",
    cta: "Start Free Trial",
    ctaStyle: "outlined",
    featured: true,
    mostPopular: true,
    features: [
      "400 credits / month — use for images (1 cr) or videos (3 cr), your choice",
      "Animated video previews — show patients smiling, laughing naturally",
      "Custom shade previews — patients compare veneer & whitening options",
      "Website lead capture tool — turn visitors into implant leads",
      "Priority support — dedicated onboarding and faster response",
      "Top up credits anytime — purchase extra credits as needed",
    ],
  },
  {
    id: "scale",
    name: "Scale",
    subtitle: "Built for DSOs & Multi-Location Groups",
    desc: "Full-scale AI smile simulation deployment for dental service organizations and multi-location dental practices.",
    customPrice: true,
    icon: "/icons/custom_plan.png",
    cta: "Contact Sales",
    ctaStyle: "primary",
    featured: false,
    mostPopular: false,
    features: [
      "Everything in Growth, plus:",
      "White-label branding — SmileReveal matches your practice identity",
      "Dedicated account manager — personal support and strategy",
      "Custom integrations — connect with your PMS, CRM & marketing tools",
      "Unlimited simulation credits — volume pricing available",
      "Top up credits anytime — purchase extra credits as needed",
    ],
  },
];

const PricingPlans = () => {
  const [isAnnual, setIsAnnual] = useState(false);

  return (
    <section className="py-24 px-3 sm:px-4 lg:px-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="text-center mb-10">
        <h2 className="text-[36px] md:text-[42px] font-bold text-slate-900 mb-4 tracking-tight leading-tight">
          Choose Your AI Smile Simulation Plan
        </h2>
        <p className="text-slate-500 text-[17px] font-medium mb-8">
          SmileReveal™ plans scale with your practice — from solo dentists to multi-location DSOs. No contracts, cancel anytime.
        </p>

        {/* Toggle + 2-months-free badge */}
        <div className="flex flex-col items-center gap-3">
          <div className="inline-flex items-center bg-slate-100 rounded-full p-1 relative">
            {/* Sliding pill */}
            <div
              className="absolute top-1 bottom-1 rounded-full bg-[#1a6fff] transition-all duration-300 ease-in-out"
              style={{
                width: "calc(50% - 4px)",
                left: isAnnual ? "calc(50%)" : "4px",
              }}
            />
            <button
              onClick={() => setIsAnnual(false)}
              className={`relative z-10 px-5 py-1.5 text-[14px] font-semibold rounded-full transition-colors duration-200 ${!isAnnual ? "text-white" : "text-slate-500"
                }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={`relative z-10 px-5 py-1.5 text-[14px] font-semibold rounded-full transition-colors duration-200 ${isAnnual ? "text-white" : "text-slate-500"
                }`}
            >
              Annual
            </button>
          </div>

          {/* 2 months free badge — animates in when annual is selected */}
          <div
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full transition-all duration-300 ${isAnnual
                ? "opacity-100 translate-y-0 bg-blue-50 border border-blue-200"
                : "opacity-0 -translate-y-1 pointer-events-none"
              }`}
          >
            <Zap className="w-3.5 h-3.5 text-blue-500" />
            <span className="text-[13px] font-semibold text-blue-600">
              2 months free — save up to 17% with annual billing
            </span>
          </div>
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
        {plans.map((plan) => {
          const Icon = plan.icon;
          const displayPrice = isAnnual
            ? Math.ceil(plan.annualPrice / 12)
            : plan.monthlyPrice;
          const credits = isAnnual ? plan.annualCredits : plan.monthlyCredits;

          return (
            <div
              key={plan.id}
              className={`rounded-[28px] p-8 flex flex-col transition-all duration-300 relative ${plan.featured
                  ? "md:scale-[1.03] z-10 shadow-xl shadow-blue-200 border border-blue-100"
                  : "z-0 border border-[#f1f5f9] shadow-lg hover:shadow-lg hover:-translate-y-0.5"
                }`}
              style={{ background: "#ffffff" }}
            >
              {/* Most Popular badge */}
              {plan.mostPopular && (
                <div className="absolute left-1/2 top-0 z-20 -translate-x-1/2 -translate-y-1/2">
                  <span
                    className="px-3 py-1 rounded-full text-[11px] font-bold tracking-wide text-white uppercase"
                    style={{
                      background:
                        "linear-gradient(135deg, #1a6fff 0%, #60a5fa 100%)",
                      boxShadow: "0 2px 8px rgba(26,111,255,0.35)",
                    }}
                  >
                    Most Popular
                  </span>
                </div>
              )}

              {/* Corner radial glows for featured card */}
              {plan.featured && (
                <>
                  <div
                    className="absolute top-0 left-0 w-72 h-72 pointer-events-none overflow-hidden rounded-[28px]"
                    style={{
                      background:
                        "radial-gradient(circle at top left, rgba(59,130,246,0.45) 0%, rgba(96,165,250,0.18) 40%, transparent 70%)",
                    }}
                  />
                  <div
                    className="absolute bottom-0 right-0 w-72 h-72 pointer-events-none overflow-hidden rounded-[28px]"
                    style={{
                      background:
                        "radial-gradient(circle at bottom right, rgba(59,130,246,0.45) 0%, rgba(96,165,250,0.18) 40%, transparent 70%)",
                    }}
                  />
                </>
              )}

              {/* Crosshatch grid that fades downward behind the icon */}
              <div
                className="absolute top-0 left-0 right-0 h-40 pointer-events-none z-0 overflow-hidden rounded-t-[28px]"
                style={{
                  backgroundImage: `linear-gradient(#bcd5f5 1px, transparent 1px), linear-gradient(90deg, #bcd5f5 1px, transparent 1px)`,
                  backgroundSize: "24px 24px",
                  opacity: plan.featured ? 0.55 : 0.38,
                  maskImage:
                    "linear-gradient(to bottom, black 0%, transparent 85%)",
                  WebkitMaskImage:
                    "linear-gradient(to bottom, black 0%, transparent 85%)",
                }}
              />

              <div
                className="w-12 h-12 rounded-[14px] flex items-center justify-center mb-5 shadow-md shadow-blue-200 relative z-10"
                style={{
                  background:
                    "radial-gradient(circle at 50% 0%, #60a5fa 0%, #0055ff 100%)",
                }}
              >
                {typeof plan.icon === "string" ? (
                  <img
                    src={plan.icon}
                    alt={plan.name}
                    className="w-6 h-6 object-contain p-0.5"
                  />
                ) : (
                  <Icon className="w-5 h-5 text-white" strokeWidth={2} />
                )}
              </div>

              {/* Plan Name + Desc */}
              <h3 className="text-[22px] font-bold mb-0.5 tracking-tight text-slate-900 relative z-10">
                {plan.name}
              </h3>
              {plan.subtitle && (
                <p className="text-[12px] font-semibold text-blue-500 mb-1 relative z-10">
                  {plan.subtitle}
                </p>
              )}
              <p className="text-[14px] leading-snug mb-6 text-slate-500 relative z-10">
                {plan.desc}
              </p>

              {/* Divider */}
              <div className="h-px mb-6 bg-slate-100 relative z-10" />

              {/* Price */}
              {plan.customPrice ? (
                <div className="mb-4 relative z-10">
                  <span className="text-[42px] font-bold leading-none text-slate-900">
                    Custom
                  </span>
                  <p className="text-[13px] font-medium mt-1 text-slate-400">Contact us for pricing</p>
                </div>
              ) : (
                <>
                  <p className="text-[13px] font-medium mb-1 text-slate-400 relative z-10">
                    Starting from
                  </p>
                  <div className="flex items-baseline gap-1 relative z-10">
                    <span
                      key={`${plan.id}-${isAnnual}`}
                      className="text-[52px] font-bold leading-none price-animate text-slate-900"
                    >
                      ${displayPrice}
                    </span>
                    <span className="text-[13px] font-medium ml-0.5 text-slate-400">
                      /mo
                    </span>
                  </div>
                  {isAnnual && (
                    <p className="text-[12px] font-medium text-slate-400 mt-0.5 mb-1 relative z-10">
                      ${plan.annualPrice} billed annually
                    </p>
                  )}
                  {/* Credits chip */}
                  <div className="flex items-center gap-1.5 mt-3 mb-4 relative z-10">
                    <div
                      className="flex items-center gap-1.5 px-3 py-1 rounded-full border"
                      style={{
                        background: "rgba(26,111,255,0.06)",
                        borderColor: "rgba(26,111,255,0.18)",
                      }}
                    >
                      <Zap className="w-3 h-3 text-blue-500" />
                      <span className="text-[12.5px] font-semibold text-blue-600">
                        {isAnnual
                          ? `${credits.toLocaleString()} credits / year`
                          : `${credits.toLocaleString()} credits / month`}
                      </span>
                    </div>
                  </div>
                </>
              )}

              {/* Features */}
              <ul className="space-y-3 mb-8 flex-1 relative z-10">
                {plan.features.map((feat, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-3 h-3 text-[#1a6fff] shrink-0" />
                    <span className="text-[14.5px] font-medium text-slate-600">
                      {feat}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <a
                href="https://app.smilereveal.io"
                className={`mt-auto w-full flex items-center justify-center gap-2 py-3.5 rounded-full text-[15px] font-semibold transition-all duration-200 group group/btn relative overflow-hidden z-10 ${plan.featured
                    ? "bg-white text-slate-900 border border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                    : "bg-[#1a6fff] text-white hover:bg-[#1562e8]"
                  }`}
              >
                {/* Shine effect - triggered on hover only */}
                <div
                  className={`absolute inset-0 w-full h-full pointer-events-none z-20 ${plan.featured ? "opacity-30" : "opacity-20"
                    }`}
                >
                  <div
                    className={`absolute top-0 h-full w-2/3 -skew-x-[20deg] opacity-0 group-hover/btn:opacity-100 group-hover/btn:animate-shine ${plan.featured
                        ? "bg-gradient-to-r from-transparent via-blue-400/40 to-transparent"
                        : "bg-gradient-to-r from-transparent via-white/60 to-transparent"
                      }`}
                  />
                </div>

                <span className="relative z-10">{plan.cta}</span>

                <div className="flex items-center text-current relative z-10">
                  <ChevronRight className="h-4 w-4 opacity-40 group-hover:translate-x-0.5 transition-transform" />
                  <ChevronRight className="h-5 w-5 opacity-70 -ml-2.5 group-hover:translate-x-0.5 transition-transform delay-75" />
                  <ChevronRight className="h-6 w-6 opacity-100 -ml-3.5 group-hover:translate-x-0.5 transition-transform delay-150" />
                </div>
              </a>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default PricingPlans;
